// The account-level settings context and its consumer hook.
//
// Split out of UserSettingsProvider.tsx deliberately: this file contains
// no JSX, so modules that only need to READ a setting can import it
// without dragging a .tsx into their type graph. Without the split, any
// pure module importing `useUserSetting` fails the root tsconfig
// (`--jsx is not set`) the moment a node-pool test pulls it in — the
// same trap use-list-view-server-preferences.ts documents about `../api`.
//
// The provider component lives next door in UserSettingsProvider.tsx and
// is the only thing that touches the network.

import { createContext, useCallback, useContext, useMemo } from "react";

import {
  readSetting,
  type UserSettingDescriptor,
  type UserSettingsBlob,
} from "./user-settings-registry";

export interface UserSettingsContextValue {
  blob: UserSettingsBlob;
  setSetting: <T>(descriptor: UserSettingDescriptor<T>, value: T) => void;
  /** False until the account row has been fetched (or the fetch failed).
   *  Consumers rendering a list generally don't need this — they get the
   *  cached or baseline value meanwhile — but it's here for anything
   *  that wants to hold off on a write until it knows the server state. */
  isLoaded: boolean;
}

export const UserSettingsContext = createContext<UserSettingsContextValue | null>(null);

/**
 * Read + write one setting. Returns the resolved value (account →
 * browser cache → baseline) and a setter that persists to both.
 *
 * Outside a provider (the public routes — sign-in, intake, upload) this
 * degrades to baseline + a no-op setter rather than throwing. Nothing on
 * those routes reads a setting, but a missing provider must never be
 * able to blank a page.
 */
/**
 * True once the account row has been fetched (or the fetch has failed and
 * we've settled on the cached/baseline answer).
 *
 * Most consumers don't need this — a list rendering one frame at its
 * default is invisible. It matters for settings that something OUTSIDE
 * React has already acted on, where applying the not-yet-loaded baseline
 * would visibly undo that work. Dark mode is the case in point: a
 * pre-hydration script paints the theme before React mounts, so a hook
 * that applied `false` while loading would strip the dark class and
 * flash light on every single load.
 *
 * Outside a provider this reports false, matching the "nothing is
 * loaded" reading rather than claiming a state it doesn't have.
 */
export function useUserSettingsLoaded(): boolean {
  return useContext(UserSettingsContext)?.isLoaded ?? false;
}

export function useUserSetting<T>(descriptor: UserSettingDescriptor<T>): [T, (value: T) => void] {
  const ctx = useContext(UserSettingsContext);
  const value = useMemo(
    () => (ctx ? readSetting(ctx.blob, descriptor) : descriptor.baseline()),
    [ctx, descriptor],
  );
  const setValue = useCallback(
    (next: T) => {
      ctx?.setSetting(descriptor, next);
    },
    [ctx, descriptor],
  );
  return [value, setValue];
}
